import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ArrayBufferConverter, DocumentData, DocumentInfo, DocumentType } from '@delab/ngx-gen-ts-ixserv/models';
import { DelabNgxDocumentsServiceDefinition, DisplayableError } from '@delab/ngx-ixconnect-components';
import { map, Observable } from 'rxjs';
import { tapError } from 'src/app/shared/rxjs-operators/tap-error.operator';
import { v4 as uuidv4 } from 'uuid';
import { DocumentsApiService } from '../../../../.api-client/services/documents-api.service';

@Injectable({ providedIn: 'root' })
export class DocumentsService implements DelabNgxDocumentsServiceDefinition {
    constructor(private readonly _documentsApiService: DocumentsApiService) {}

    getDocumentTypes(): Observable<DocumentType[]> {
        return this._documentsApiService.documentsGetDocumentTypes({ 'x-trace-id': uuidv4() }).pipe(
            map((docTypesDto): DocumentType[] => {
                if (typeof docTypesDto === 'undefined') {
                    throw Error();
                }
                return docTypesDto.map((docTypeDto) => new DocumentType(docTypeDto));
            }),
        );
    }

    getDocumentsForCase(casId: number): Observable<DocumentInfo[]> {
        return this._documentsApiService.documentsGetDocumentsForCase({ casId, 'x-trace-id': uuidv4() }).pipe(
            map((docInfosDto): DocumentInfo[] => {
                if (typeof docInfosDto === 'undefined') {
                    throw Error();
                }
                return docInfosDto.map((docInfoDto) => new DocumentInfo(docInfoDto));
            }),
        );
    }

    getDocumentsForPatient(perId: number): Observable<DocumentInfo[]> {
        return this._documentsApiService.documentsGetDocumentsForPatient({ perId, 'x-trace-id': uuidv4() }).pipe(
            map((docInfosDto): DocumentInfo[] => {
                if (typeof docInfosDto === 'undefined') {
                    throw Error();
                }
                return docInfosDto.map((docInfoDto) => new DocumentInfo(docInfoDto));
            }),
        );
    }

    downloadDocument(recId: number): Observable<ArrayBuffer | undefined> {
        return this._documentsApiService.documentsDownloadDocument({ recId, 'x-trace-id': uuidv4() }).pipe(
            map((response) => {
                if (typeof response.data === 'undefined') {
                    throw new Error('No data found for recID');
                }

                return new ArrayBufferConverter().deserialize(response.data) || undefined;
            }),
        );
    }

    uploadDocumentForCase(data: DocumentData, casId: number): Observable<DocumentInfo | undefined> {
        return this._documentsApiService.documentsUploadDocumentForCase({ casId, body: data.toJSON(), 'x-trace-id': uuidv4() }).pipe(
            map((docInfosDto): DocumentInfo => {
                if (typeof docInfosDto === 'undefined') {
                    throw Error();
                }
                return new DocumentInfo(docInfosDto);
            }),
            tapError((error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                    if (error.status === 400 && error.error?.data?.message === 'forbidden-document-type') {
                        throw new DisplayableError(
                            'No permission to upload this document type',
                            'Keine Berechtigung zum Hochladen dieses Dokumententyps.',
                        );
                    }

                    if (error.status !== 0) {
                        throw new DisplayableError(
                            `Server error (HTTP status code ${error.status})`,
                            `Serverfehler (HTTP-Statuscode ${error.status})`,
                        );
                    }
                }
            }),
        );
    }
}
